// recorder.js — MediaRecorder wrapper for the Self-Recording exercise.
//
// Mirrors KFERecordingManager.m / Self-Record's responsibilities:
//   • request mic permission
//   • record audio to a Blob
//   • expose a real-time level meter (Web Audio AnalyserNode)
//   • stop and return the Blob (audio/webm;codecs=opus on Chromium/Firefox,
//     audio/mp4;codecs=mp4a on Safari — both round-trip server-side)
//
// Use:
//   const rec = new Recorder();
//   await rec.start();              // user gesture required for permission
//   rec.onLevel = lvl => …;
//   const blob = await rec.stop();  // keeps the mic stream alive for re-use
//   …
//   rec.release();                  // full teardown on component disconnect
//
// STREAM LIFECYCLE (Brief 173 Sub-fix 6 + 1). The mic `MediaStream` and the
// metering `AudioContext` are acquired ONCE per Recorder instance — on the first
// `start()` — and REUSED for every subsequent record/stop cycle. They are torn
// down only by `release()` (the SR screen calls it from disconnectedCallback).
// Two reasons, both surfaced on Chris's 2026-06-20 iPhone walk:
//   • Sub-fix 6 — a fresh `getUserMedia` per record tap re-prompts the OS mic
//     permission dialog after the previous stream is auto-released by Safari.
//     Holding one stream for the mount shows the prompt at most once.
//   • Sub-fix 1 — a fresh `AudioContext` per record tap churned iOS Safari's
//     small (~4) simultaneous-context budget; an un-released context from a
//     prior mount could stall the next acquisition, the most likely root of the
//     "re-tap the episode → screen never loads" stall. One context per mount,
//     deterministically closed on disconnect, removes that churn.
// Only the `MediaRecorder` is per-recording (it is single-use after `stop()`).
//
// STREAM-WARMING HEARTBEAT (Brief 173b). iOS Safari/WebKit's audio-session
// manager reclaims an *idle* MediaStream at the OS level after ~5 min — freeing
// the "mic in use" indicator and saving battery — regardless of whether JS still
// references the stream. The next record tap then re-prompts the OS permission
// dialog (Sub-fix 6 resolved the per-tap churn but not this long-idle reclaim).
// The mitigation: touch the stream's tracks at a low cadence so the OS accounts
// the stream "in use" and skips the reclaim.
//
// IMPORTANT — track metadata read, NOT analyser read (Brief 173b post-walk fix
// 2026-06-28). The original heartbeat read `analyser.getByteFrequencyData()`,
// which requires the AudioContext to be connected to a MediaStreamSource. On
// Chris's iOS Safari/WebKit 18.x walk the heartbeat DID successfully suppress
// the 5-min idle re-prompt — but having the audio graph connected during
// recording caused iOS to apply different AGC (auto gain control) and dropped
// MediaRecorder's recorded gain to near-inaudible. Fix: switch the heartbeat
// to `track.getSettings()` — a no-op metadata read that touches the track
// without routing through any audio-graph node, so iOS still accounts the
// stream "in use" but AGC is unaffected.
//
// `_startHeartbeat()` is started from `ensureStream()` (so it runs as soon as
// the stream exists, before the first record) and stopped ONLY by `release()`
// (NOT `stop()`/`cancel()`, which keep the stream alive for reuse — so the
// heartbeat must keep warming it).

// Heartbeat cadence. 1.5s is fast enough to keep the stream "active" per WebRTC
// community reports, slow enough to be battery-friendly (one track-metadata
// read per tick). Tunable from later on-device observation. Tested against iOS
// Safari/WebKit 18.x (Chris's iPhone, 2026-06): 5 min idle suppression with
// `getByteFrequencyData()` confirmed but AGC regression observed; switched to
// `getSettings()` 2026-06-28 — needs re-verification both that the re-prompt
// stays suppressed AND that recording gain is restored.
export const HEARTBEAT_MS = 1500;

// getUserMedia capture constraints (Brief 183, 2026-06-29).
//
// `autoGainControl: false` is the targeted fix for Chris's 2026-06-29 iPhone
// walk: the recording-level meter sat "almost hard left" during normal speech
// and playback was quiet on the phone speaker. iOS Safari's default AGC
// compresses dynamic range and attenuates a steady speaking voice, so the
// captured signal — what MediaRecorder writes AND what the analyser meters —
// comes through low.
//
// This MIRRORS iOS canon, it does not diverge from it (CLAUDE.md Rule #9). The
// native app's `KFERecordingManager.m` records under a plain
// `AVAudioSessionCategoryRecord` with no `Measurement`/`VoiceChat` mode and no
// `setInputGain:` call — i.e. raw mic capture with NO auto-gain/voice-processing
// and NO manual gain compensation. `autoGainControl: false` is the Web Audio
// equivalent of that no-AGC capture. (A boosting GainNode — Brief 183 Step D3 —
// would have ADDED gain compensation iOS never does, so it is deliberately NOT
// taken; see docs/183-sr-recording-input-level-low-handover.md.)
//
// `autoGainControl` is a HINT (Brief 183 Gotcha 1) — Safari may ignore it; this
// is the most-targeted constraint change (Step D1) and its effect is verified
// on-device by the iPhone walk, not by these unit tests. `echoCancellation` /
// `noiseSuppression` stay false as before (Brief 173).
export const CAPTURE_CONSTRAINTS = Object.freeze({
  echoCancellation: false,
  noiseSuppression: false,
  autoGainControl: false,
});

/**
 * RMS amplitude of a Web Audio time-domain buffer (bytes centred on 128, the
 * `AnalyserNode.getByteTimeDomainData` representation of zero). Returns 0..~1.
 * Extracted from the level loop (Brief 183) so the meter input can be unit
 * tested against a known-amplitude signal without driving requestAnimationFrame.
 * @param {Uint8Array} buf
 * @returns {number}
 */
export function rmsFromTimeDomain(buf) {
  if (!buf || buf.length === 0) return 0;
  let sum = 0;
  for (let i = 0; i < buf.length; i++) {
    const v = (buf[i] - 128) / 128;
    sum += v * v;
  }
  return Math.sqrt(sum / buf.length);
}

export class Recorder extends EventTarget {
  constructor() {
    super();
    /** @type {MediaStream | null} */ this.stream = null;     // one per instance (mount)
    /** @type {MediaRecorder | null} */ this.recorder = null; // per-recording (single-use)
    /** @type {AudioContext | null} */ this.ctx = null;       // one per instance (mount)
    /** @type {AnalyserNode | null} */ this.analyser = null;
    /** @type {Blob[]} */ this.chunks = [];
    /** @type {number | null} */ this._levelTimer = null;
    /** @type {number | null} */ this._heartbeatTimer = null; // stream-warming interval (Brief 173b)
    /** @type {((lvl: number) => void) | null} */ this.onLevel = null;
  }

  /**
   * Acquire the mic stream ONCE per instance; idempotent. Must be invoked from a
   * user gesture (the first record tap) so Safari grants permission. Subsequent
   * record cycles reuse the cached stream — no second OS prompt (Sub-fix 6).
   * @returns {Promise<MediaStream>}
   */
  async ensureStream() {
    if (this.stream) return this.stream;
    this.stream = await navigator.mediaDevices.getUserMedia({ audio: CAPTURE_CONSTRAINTS });
    // Start warming the stream immediately (Brief 173b) so iOS Safari's idle
    // reclaim is (hopefully) deferred even when the learner records once and
    // then sits idle for minutes. Survives stop()/cancel(); torn down only by
    // release().
    this._startHeartbeat();
    return this.stream;
  }

  /**
   * Lazily build the metering AudioContext + analyser on `this.stream`. Created
   * ONCE and reused for the instance's lifetime (Sub-fix 1 — no per-tap context
   * churn). Called from `start()`. Originally extracted (Brief 173b) so the
   * heartbeat could build the same graph; that path is gone (2026-06-28 the
   * heartbeat switched to `track.getSettings()` to avoid an AGC interaction
   * with MediaRecorder). The helper stays as a single-callsite no-op-if-present
   * for clarity. No-op once `this.ctx` exists, or if there is no stream yet.
   */
  _ensureAudioGraph() {
    if (this.ctx || !this.stream) return;
    this.ctx = new AudioContext();
    const src = this.ctx.createMediaStreamSource(this.stream);
    this.analyser = this.ctx.createAnalyser();
    this.analyser.fftSize = 1024;
    src.connect(this.analyser);
  }

  /**
   * Stream-warming heartbeat (Brief 173b). Idempotent. Each tick calls
   * `track.getSettings()` — a no-op metadata read on the audio track — so iOS
   * accounts the mic stream "in use" and skips the long-idle reclaim WITHOUT
   * touching any audio-graph node (which on iOS Safari 18.x changes AGC and
   * silences the MediaRecorder; see the file header for the fix history).
   */
  _startHeartbeat() {
    if (this._heartbeatTimer != null) return; // one heartbeat per instance
    this._heartbeatTimer = /** @type {any} */ (setInterval(() => {
      if (!this.stream) return;       // defensive — released between ticks
      const track = this.stream.getAudioTracks?.()[0];
      if (track) track.getSettings();
    }, HEARTBEAT_MS));
  }

  _stopHeartbeat() {
    if (this._heartbeatTimer != null) clearInterval(/** @type {number} */ (this._heartbeatTimer));
    this._heartbeatTimer = null;
  }

  async start() {
    if (this.recorder) throw new Error("[recorder] already running");
    // One-slot transient model (Brief 053): every start() begins with an
    // empty chunks array so back-to-back recordings on a reused Recorder
    // instance cannot concatenate into a single blob.
    this.chunks = [];
    await this.ensureStream();
    const stream = /** @type {MediaStream} */ (this.stream);
    const mime = pickMimeType();
    this.recorder = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
    this.recorder.addEventListener("dataavailable", (e) => {
      if (e.data && e.data.size) this.chunks.push(e.data);
    });
    this.recorder.start(100);

    // The AudioContext + analyser back the level meter. Created ONCE and reused
    // for the instance's lifetime (Sub-fix 1 — no per-tap context churn). The
    // build itself now lives in `_ensureAudioGraph()` (Brief 173b) so the
    // heartbeat can construct the same graph if it ticks before the first
    // record; this call is a no-op when the context already exists. On iOS the
    // context can come back suspended; resume it so the meter ticks.
    this._ensureAudioGraph();
    if (this.ctx && this.ctx.state === "suspended" && typeof this.ctx.resume === "function") {
      try { await this.ctx.resume(); } catch { /* no-op */ }
    }
    this._startLevelLoop();
  }

  _startLevelLoop() {
    const analyser = this.analyser;
    if (!analyser) return;
    const buf = new Uint8Array(analyser.fftSize);
    const tick = () => {
      // Stop the loop once the recording ends (recorder cleared) or the context
      // is released (analyser cleared) — never leak a rAF across recordings.
      if (!this.analyser || !this.recorder) return;
      this.analyser.getByteTimeDomainData(buf);
      const rms = rmsFromTimeDomain(buf);
      this.onLevel?.(rms);
      this.dispatchEvent(new CustomEvent("level", { detail: rms }));
      this._levelTimer = /** @type {any} */ (requestAnimationFrame(tick));
    };
    tick();
  }

  _stopLevelLoop() {
    if (this._levelTimer != null) cancelAnimationFrame(/** @type {number} */ (this._levelTimer));
    this._levelTimer = null;
  }

  /** @returns {Promise<Blob>} */
  stop() {
    if (!this.recorder) return Promise.resolve(new Blob());
    return new Promise((resolve) => {
      this.recorder?.addEventListener("stop", () => {
        const type = this.recorder?.mimeType || "audio/webm";
        const blob = new Blob(this.chunks, { type });
        this._stopLevelLoop();
        // Discard the per-recording MediaRecorder, but KEEP the mic stream +
        // AudioContext alive so the next record on this instance reuses them
        // (Sub-fix 6 — no second permission prompt).
        this.recorder = null;
        resolve(blob);
      }, { once: true });
      this.recorder?.stop();
    });
  }

  /**
   * Abort an in-flight recording WITHOUT producing a blob, keeping the mic
   * stream + AudioContext alive — a word switch within the same mount. Use
   * `release()` for full teardown on disconnect.
   */
  cancel() {
    this._stopLevelLoop();
    if (this.recorder) {
      try { this.recorder.stop(); } catch { /* no-op */ }
      this.recorder = null;
    }
    this.chunks = [];
  }

  /**
   * Full teardown (Brief 173): stop the mic tracks + close the AudioContext so
   * the OS mic indicator clears and the context budget is freed for the next
   * mount. Called from the SR screen's disconnectedCallback. Idempotent.
   */
  release() {
    this.cancel();
    // The heartbeat is stopped HERE ONLY — not in stop()/cancel(), which keep
    // the stream alive for reuse and so must keep warming it (Brief 173b).
    this._stopHeartbeat();
    this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = null;
    this.ctx?.close().catch(() => {});
    this.ctx = null;
    this.analyser = null;
  }
}

function pickMimeType() {
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4;codecs=mp4a.40.2",
    "audio/mp4",
  ];
  return candidates.find((m) => MediaRecorder.isTypeSupported?.(m));
}
